import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useNavigate } from 'react-router-dom';

import { Loader } from 'common/components/Loader/Loader';
import SideDrawer from 'common/components/SideDrawer/SideDrawer';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { AI_ACTIONS, AI_TYPE } from 'common/constants/AISimulationConstants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import { rrcpDashboardRoutes } from 'common/constants/rrcp-dashboard-routes';
import { GENERIC_ERROR_MSG } from 'common/constants/TextContent';
import { RootState } from 'common/store/AppStore';
import {
	setSimulationCommonShareDrawer,
	setToastMessage,
} from 'common/store/reducer/commonReducer';
import { urlWithTenant } from 'common/utils/utilFunctions';

import {
	postAIModal,
	putAIModal,
	shareAISimulation,
	saveAIRecipes,
	getSharedDataByID,
} from 'features/AiSimulation/api';
import { IAIShareProps } from 'features/AiSimulation/api/type-def';

import SaveSimulationDetails from '../SaveSimulationDetails/SaveSimulationDetails';

import styles from './AIShare.module.scss';

const { OK, CREATED, CONFLICT } = HTTP_STATUS_CODES;
const { MODEL } = AI_TYPE;
const { SHARE } = AI_ACTIONS;

const AIShare = (props: IAIShareProps) => {
	const {
		aiSharedData,
		type: shareType,
		saveSimulationComment,
		typeBtn,
	} = props;
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [isLoading, setIsLoading] = useState(false);

	const shareSimulationModal = useSelector(
		(state: RootState) => state.common.simulationCommonShareDrawer,
	);

	const { isToastOpen, message, severity } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);

	const loggedInUser = useSelector(
		(state: RootState) => state?.common?.loggedInUser,
	);

	const displayToast = (message: string, severity: string) => {
		dispatch(
			setToastMessage({
				message,
				severity,
				isToastOpen: true,
			}),
		);
	};

	const shareSimulationHandler = async (
		name: string,
		comment: string,
		recipients: any,
	) => {
		const aiData: any = {
			...aiSharedData,
			name,
		};

		let response: any;
		if (shareType === MODEL) {
			if (aiData.id) {
				response = await putAIModal(aiData, aiData.id);
			} else {
				response = await postAIModal(aiData);
			}
		} else {
			response = await saveAIRecipes(aiData);
			if (
				(response?.statusCode === OK || response?.statusCode === CREATED) &&
				saveSimulationComment
			) {
				saveSimulationComment(name, response?.responseBody?.id, true);
			}
		}

		shareSavedSimulation(response, name, comment, recipients);

		dispatch(setSimulationCommonShareDrawer(false));
	};

	const shareSavedSimulation = async (
		response: any,
		name: string,
		comment: string,
		recipients: any,
	) => {
		if (response?.statusCode === OK || response?.statusCode === CREATED) {
			const shareData: any = {
				simulationId: response?.responseBody?.id,
				simulationName: name,
				type: shareType,
				recipients,
				sharedBy: loggedInUser?.userId as string,
				comment,
			};

			const shareResponse = await shareAISimulation(shareData);
			if (
				shareResponse?.statusCode === OK ||
				shareResponse?.statusCode === CREATED
			) {
				navigate(urlWithTenant(`/${rrcpDashboardRoutes.simulationArena}`), {
					state: {
						shouldShowToast: true,
						message: 'The details saved and shared  successfully',
					},
				});
			} else {
				displayToast(GENERIC_ERROR_MSG, 'error');
			}
		} else if (response?.statusCode === CONFLICT) {
			displayToast('Name already exists.', 'error');
		} else {
			displayToast(GENERIC_ERROR_MSG, 'error');
		}
	};

	const handleToast = useCallback(() => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	}, []);

	const [existingSharedData, setExistingSharedData] = useState<any>();

	const fetchExistingSharedData = async (existingID: string) => {
		const existingSharedRes = await getSharedDataByID(existingID);
		if (existingSharedRes?.statusCode === OK) {
			setExistingSharedData(existingSharedRes.responseBody);
		} else {
			setExistingSharedData({});
		}
	};

	useEffect(() => {
		setIsLoading(true);
		setExistingSharedData({});
		if (shareSimulationModal) {
			fetchExistingSharedData(aiSharedData?.id);
			setIsLoading(false);
		} else {
			setIsLoading(false);
		}
	}, [aiSharedData.id, shareSimulationModal, typeBtn]);

	return (
		<>
			<SideDrawer
				className={styles['save-drawer']}
				drawerState={shareSimulationModal}
			>
				{!isLoading ? (
					<SaveSimulationDetails
						shareSimulation={shareSimulationHandler}
						type={SHARE}
						name={aiSharedData.name}
						existingSharedData={existingSharedData}
					/>
				) : (
					<Loader show={isLoading} className={styles['layout-loader']} />
				)}
			</SideDrawer>
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleToast}
				vertical="top"
				horizontal="right"
			/>
		</>
	);
};

export default AIShare;
